import { useContext, useEffect, useState } from 'react'

import {
  Box,
  Button,
  Icon,
  Input,
  Pagination,
  Table,
  TableHeader,
  Text,
} from '@fairfashion/design-system'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { useDebounceValue } from '../../../../hooks/useDebounceValue'
import { ICompanyInvoices } from '../../../../sdk/invoices/invoice.types'
import { docMask } from '../../../../utilities/masks/brasilianDocuments'
import { OriginInvoiceContext } from '../../origin-invoice.context'

import { InvoiceCodesTableProps } from './invoice-codes-table.types'

export const InvoiceCodeTable = ({ provider }: InvoiceCodesTableProps) => {
  const { invoiceCodesData, actions } = useContext(OriginInvoiceContext)
  const navigate = useNavigate()

  const theme = useTheme()
  const [page, setPage] = useState(1)
  const [filterCode, setFilterCode] = useState<string>('')

  const limit = 50
  const getInvoices = () => {
    provider &&
      actions.getAllInvoices(provider?.taxId ?? '', page >= 1 ? page : 1, limit)
  }
  useEffect(() => {
    getInvoices()
  }, [page, provider])

  const headers: TableHeader[] = [
    {
      key: 'code',
      label: 'Nota fiscal',
      width: '180px',
    },
    {
      key: 'clientName',
      label: 'Cliente da NF',
    },
    {
      key: 'clientTaxId',
      label: 'CNPJ do cliente da NF',
      customRender: (data) => {
        return (
          <Text size="small" color="grey_3">
            {docMask(data.clientTaxId)}
          </Text>
        )
      },
    },
    {
      key: 'showMore',
      label: 'Ver',
      width: '50px',
      customRender: (data) => {
        return (
          <Button
            onClick={() =>
              provider &&
              navigate(`/invoices/${provider.taxId}/${data.code.trim()}`)
            }
            variant="text"
            padding="0"
          >
            <Icon name="menu_search" size={22} color={'secondary'} />
          </Button>
        )
      },
    },
  ]
  const handleChangeFilter = (value: string) => {
    if (!provider) return
    actions.getAllInvoices(provider?.taxId ?? '', 1, limit, value?.trim())
  }

  const filterCodeDebounced = useDebounceValue(filterCode, 1000)

  useEffect(() => {
    handleChangeFilter(filterCodeDebounced)
  }, [filterCodeDebounced])
  return (
    <Box direction="column" width="100%" gap={4}>
      <Box
        flex={1}
        direction="column"
        width="100%"
        backgroundColor="white"
        borderRadius={theme.border_radius.box}
        border={{
          color: 'grey_5',
          width: 1,
        }}
        boxShadow={theme.box_shadow.card}
      >
        {provider && (
          <Box
            width="100%"
            padding={3}
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Box justifyContent="space-between" width="100%">
              <Box direction="column" flex={1}>
                <Text fontWeight={500}>{provider.name}</Text>
                <Text color="grey_3">
                  CNPJ: {docMask(provider?.taxId ?? '')}
                </Text>
                <Text color="grey_3">Notas fiscais emitidas.</Text>
              </Box>
              <Box flex={1} maxWidth="300px">
                <Input
                  adornment={<Icon name="search" />}
                  placeholder="Buscar nota fiscal"
                  onChange={(value) => setFilterCode(value.target.value)}
                />
              </Box>
            </Box>
          </Box>
        )}
        <Table
          headers={headers}
          data={
            provider
              ? (invoiceCodesData?.data ?? ([] as ICompanyInvoices[]))
              : ([] as ICompanyInvoices[])
          }
          borderRadius={`0 0 ${theme.border_radius.box} ${theme.border_radius.box}`}
          footer={
            <Box justifyContent="center" width="100%" paddingY={1}>
              {provider && invoiceCodesData?.count ? (
                <Pagination
                  total={Math.ceil(invoiceCodesData.count / limit)}
                  current={page >= 1 ? page : 1}
                  onChange={(_page) => setPage(_page)}
                />
              ) : (
                <Box padding={8}>
                  {provider
                    ? 'Nenhuma nota fiscal encontrada'
                    : 'Nenhum fornecedor selecionado'}
                </Box>
              )}
            </Box>
          }
        />
      </Box>
    </Box>
  )
}
